import React from 'react'
import { useCandidateChat } from './CandidateChatContext'
import { useTranslation } from '../../../hooks/useTranslation'
import { Video, VideoOff, X, Mute, Unmute } from '../../icons/YnsIcon'
import { Button, Typography } from '../../_atoms'
import { Stack } from '../../_layouts'
import { getCareerDevelopmentClassName } from '../utils/careerDevelopmentStyles'

/**
 * ChatHeader component - displays the chat header with title and action buttons
 * Based on the op_next design from app/views/home/coach_chat/content/_chat_header.html.erb
 *
 * @param {Object} props
 * @param {Object} props.selectedCoach - The currently selected coach
 * @param {string} props.chatMode - Current chat mode (text, audio, video)
 * @param {string} props.chatStatus - Current chat status
 * @param {boolean} props.showControls - Whether to show pause/mute/end controls (default: true)
 * @param {boolean} props.showCloseButton - Show only the close/X control (e.g. missed call screen)
 * @param {Function} props.onEndChat - Optional callback for end chat action
 * @param {boolean} props.deloitteUser - Whether user is a Deloitte user (shows "Advisor" instead of "Coach")
 * @param {boolean} props.userCareerDevelopment - Whether user is career development (affects pause icon)
 */
const ChatHeader = ({
  selectedCoach,
  chatMode,
  chatStatus: _chatStatus,
  showControls = true,
  showCloseButton = false,
  onEndChat,
  deloitteUser = false,
  userCareerDevelopment = false,
}) => {
  const { candidateChatManager, connectionState } = useCandidateChat()
  const { t } = useTranslation('coach_chat')

  // Determine chat title based on user type
  const getChatTitle = () => {
    if (selectedCoach) {
      return selectedCoach.displayName
    }
    return deloitteUser
      ? t('chat_header.advisor_chat', { defaultValue: 'Advisor Chat' })
      : t('chat_header.coach_chat', { defaultValue: 'Coach Chat' })
  }

  // Determine if we should show pause/unpause buttons (only for video)
  const showPauseControls = showControls && chatMode === 'video'

  // Get video pause state from connection state (now includes media state)
  const isPaused = connectionState?.videoPaused || false

  // Get audio mute state from connection state (now includes media state)
  const isMuted = connectionState?.audioMuted || false

  // Determine if we should show mute controls (for audio/video)
  const showMuteControls =
    showControls && (chatMode === 'audio' || chatMode === 'video')

  // Handle end chat action
  const handleEndChat = () => {
    if (onEndChat) {
      onEndChat()
    } else if (candidateChatManager.endChat) {
      candidateChatManager.endChat()
    }
  }

  // Helper function to get common button props
  const getButtonProps = (id, title, ariaLabel, onClick) => ({
    id,
    variant: 'ghost',
    size: 'icon',
    className: getCareerDevelopmentClassName(
      userCareerDevelopment,
      'h-8 w-8 bg-transparent text-white hover:bg-white hover:bg-opacity-10 hover:text-white',
    ),
    title: t(title),
    'aria-label': t(ariaLabel),
    onClick,
  })

  return (
    <div id="chat_header" className="h-16 bg-intoo-green-medium-700 px-5 pr-1">
      <Stack
        direction="row"
        spacing={0}
        className="h-full items-center justify-between"
      >
        <div className="flex items-center">
          <Typography variant="title4" className="text-white">
            {getChatTitle()}
          </Typography>
        </div>

        {(showControls || showCloseButton) && (
          <Stack direction="row" spacing={2} className="items-center">
            {/* Pause/Unpause controls - only for video/audio */}
            {showControls && showPauseControls && (
              <Button
                {...getButtonProps(
                  isPaused ? 'btn_unpause_chat' : 'btn_pause_chat',
                  isPaused
                    ? 'career_development.resume_chat'
                    : 'career_development.pause_chat',
                  isPaused
                    ? 'career_development.resume_chat'
                    : 'career_development.pause_chat',
                  () => {
                    isPaused
                      ? candidateChatManager.unpauseChat()
                      : candidateChatManager.pauseChat()
                  },
                )}
                aria-pressed={isPaused}
              >
                {isPaused ? (
                  <Video className="h-4 w-4" />
                ) : (
                  <VideoOff className="h-4 w-4" />
                )}
              </Button>
            )}

            {/* Mute/Unmute controls - only for audio/video */}
            {showControls && showMuteControls && (
              <Button
                {...getButtonProps(
                  isMuted ? 'btn_unmute_chat' : 'btn_mute_chat',
                  isMuted
                    ? 'career_development.unmute_chat'
                    : 'career_development.mute_chat',
                  isMuted
                    ? 'career_development.unmute_chat'
                    : 'career_development.mute_chat',
                  () => {
                    isMuted
                      ? candidateChatManager.unmuteChat()
                      : candidateChatManager.muteChat()
                  },
                )}
                aria-pressed={isMuted}
              >
                {isMuted ? (
                  <Unmute className="h-4 w-4" />
                ) : (
                  <Mute className="h-4 w-4" />
                )}
              </Button>
            )}

            {/* End/close button — full controls or close-only (missed call) */}
            <Button
              {...getButtonProps(
                'btn_end_chat',
                'career_development.end_chat',
                'career_development.end_chat',
                handleEndChat,
              )}
            >
              <X className="h-6 w-6" />
            </Button>
          </Stack>
        )}
      </Stack>
    </div>
  )
}

export default ChatHeader
